import type { FolderUploadPanelCopy } from './folder-upload-panel-copy';

/** Upload panel copy for Arabic, Indic and East Asian locales. */
export const FOLDER_UPLOAD_PANEL_COPY_EASTERN: Record<
  'ar' | 'ko' | 'ja' | 'hi' | 'zh' | 'zh-TW',
  FolderUploadPanelCopy
> = {
  ar: {
    regionLabel: 'عمليات الرفع',
    filterLabel: 'الملفات المعروضة',
    titleUploading: 'جارٍ الرفع إلى {folder}',
    titleDone: 'تم الرفع إلى {folder}',
    titleFailed: 'لم يتم رفع بعض الملفات إلى {folder}',
    titleCancelled: 'تم إلغاء الرفع إلى {folder}',
    titleCancelling: 'جارٍ إلغاء الرفع إلى {folder}…',
    titleBlocked: 'تعذّر الرفع إلى {folder}',
    progressSummary: '{done} من {total} ملفات · {bytesDone} من {bytesTotal}',
    timeLeft: 'متبقٍ ~{time}',
    summaryUploaded: 'تم رفع {count}',
    summaryFailed: 'فشل {count}',
    summaryCancelled: 'أُلغي {count}',
    view: 'عرض',
    showFiles: 'إظهار الملفات',
    hideFiles: 'إخفاء الملفات',
    cancelAll: 'إلغاء الكل',
    retryFailed: 'إعادة محاولة الفاشلة',
    retrySome: 'إعادة المحاولة ({count})',
    dismiss: 'إغلاق',
    cancelFile: 'إلغاء {name}',
    retryFile: 'إعادة محاولة {name}',
    filterAll: 'الكل',
    filterFailed: 'الفاشلة ({count})',
    statusQueued: 'في الانتظار',
    statusUploading: '{loaded} من {total}',
    statusRegistering: 'جارٍ الإنهاء…',
    statusDone: 'تم الرفع',
    statusCancelled: 'تم إلغاء الرفع',
    emptyFoldersFailed: 'مجلدات فارغة لم تُنشأ: {count}',
    progressLabel: 'تقدّم الرفع',
    pasteMenuItem: 'لصق',
    pastedImageName: 'صورة ملصقة {date}',
    pastedTextName: 'نص ملصق {date}',
    reasons: {
      storage_limit: 'لا توجد مساحة تخزين كافية في مساحة العمل',
      uploads_busy: 'هناك عدد كبير جدًا من عمليات الرفع الجارية',
      network: 'انقطع الاتصال',
      unverified: 'تغيّر أثناء الرفع أو تعذّر التحقق منه',
      permission: 'لا يمكنك إضافة ملفات إلى هذا المجلد',
      invalid_path: 'لا يمكن استخدام اسم المجلد هذا',
      unreadable: 'تعذّرت قراءة هذا الملف',
      too_large: 'أكبر من أن يُرفع',
      active_content: 'المحتوى لا يطابق نوع الملف',
      workspace_loading: 'كانت مساحة العمل لا تزال قيد التحميل. حاول مرة أخرى.',
      unknown: 'فشل الرفع',
    },
  },
  ko: {
    regionLabel: '업로드',
    filterLabel: '표시할 파일',
    titleUploading: '{folder}에 업로드 중',
    titleDone: '{folder}에 업로드됨',
    titleFailed: '일부 파일이 {folder}에 업로드되지 않았습니다',
    titleCancelled: '{folder} 업로드가 취소됨',
    titleCancelling: '{folder}(으)로 업로드 취소 중…',
    titleBlocked: '{folder}에 업로드할 수 없습니다',
    progressSummary: '파일 {done}/{total}개 · {bytesDone}/{bytesTotal}',
    timeLeft: '약 {time} 남음',
    summaryUploaded: '{count}개 업로드됨',
    summaryFailed: '{count}개 실패',
    summaryCancelled: '{count}개 취소됨',
    view: '보기',
    showFiles: '파일 보기',
    hideFiles: '파일 숨기기',
    cancelAll: '모두 취소',
    retryFailed: '실패한 항목 다시 시도',
    retrySome: '{count}개 다시 시도',
    dismiss: '닫기',
    cancelFile: '{name} 취소',
    retryFile: '{name} 다시 시도',
    filterAll: '전체',
    filterFailed: '실패 ({count})',
    statusQueued: '대기 중',
    statusUploading: '{loaded}/{total}',
    statusRegistering: '마무리 중…',
    statusDone: '업로드됨',
    statusCancelled: '업로드 취소됨',
    emptyFoldersFailed: '만들지 못한 빈 폴더: {count}',
    progressLabel: '업로드 진행률',
    pasteMenuItem: '붙여넣기',
    pastedImageName: '붙여넣은 이미지 {date}',
    pastedTextName: '붙여넣은 텍스트 {date}',
    reasons: {
      storage_limit: '워크스페이스 저장 공간이 부족합니다',
      uploads_busy: '진행 중인 업로드가 너무 많습니다',
      network: '네트워크 연결이 끊겼습니다',
      unverified: '업로드 중 변경되었거나 확인할 수 없습니다',
      permission: '이 폴더에는 파일을 추가할 수 없습니다',
      invalid_path: '이 폴더 이름은 사용할 수 없습니다',
      unreadable: '이 파일을 읽을 수 없습니다',
      too_large: '너무 커서 업로드할 수 없습니다',
      active_content: '내용이 파일 형식과 일치하지 않습니다',
      workspace_loading:
        '워크스페이스를 아직 불러오는 중이었습니다. 다시 시도하세요.',
      unknown: '업로드 실패',
    },
  },
  ja: {
    regionLabel: 'アップロード',
    filterLabel: '表示するファイル',
    titleUploading: '{folder} にアップロード中',
    titleDone: '{folder} にアップロードしました',
    titleFailed: '一部のファイルを {folder} にアップロードできませんでした',
    titleCancelled: '{folder} へのアップロードをキャンセルしました',
    titleCancelling: '{folder} へのアップロードをキャンセル中…',
    titleBlocked: '{folder} にアップロードできません',
    progressSummary: '{done}/{total} ファイル · {bytesDone}/{bytesTotal}',
    timeLeft: '残り約 {time}',
    summaryUploaded: '{count} 件アップロード済み',
    summaryFailed: '{count} 件失敗',
    summaryCancelled: '{count} 件キャンセル',
    view: '表示',
    showFiles: 'ファイルを表示',
    hideFiles: 'ファイルを隠す',
    cancelAll: 'すべてキャンセル',
    retryFailed: '失敗分を再試行',
    retrySome: '{count} 件を再試行',
    dismiss: '閉じる',
    cancelFile: '{name} をキャンセル',
    retryFile: '{name} を再試行',
    filterAll: 'すべて',
    filterFailed: '失敗 ({count})',
    statusQueued: '待機中',
    statusUploading: '{loaded}/{total}',
    statusRegistering: '仕上げ中…',
    statusDone: 'アップロード済み',
    statusCancelled: 'アップロードをキャンセルしました',
    emptyFoldersFailed: '作成できなかった空のフォルダー: {count}',
    progressLabel: 'アップロードの進行状況',
    pasteMenuItem: '貼り付け',
    pastedImageName: '貼り付けた画像 {date}',
    pastedTextName: '貼り付けたテキスト {date}',
    reasons: {
      storage_limit: 'ワークスペースのストレージが不足しています',
      uploads_busy: '進行中のアップロードが多すぎます',
      network: 'ネットワークが中断されました',
      unverified: 'アップロード中に変更されたか、確認できませんでした',
      permission: 'このフォルダーにはファイルを追加できません',
      invalid_path: 'このフォルダー名は使用できません',
      unreadable: 'このファイルを読み取れませんでした',
      too_large: '大きすぎてアップロードできません',
      active_content: '内容がファイル形式と一致しません',
      workspace_loading:
        'ワークスペースを読み込み中でした。もう一度お試しください。',
      unknown: 'アップロードに失敗しました',
    },
  },
  hi: {
    regionLabel: 'अपलोड',
    filterLabel: 'दिखाने वाली फ़ाइलें',
    titleUploading: '{folder} में अपलोड हो रहा है',
    titleDone: '{folder} में अपलोड हो गया',
    titleFailed: 'कुछ फ़ाइलें {folder} में अपलोड नहीं हुईं',
    titleCancelled: '{folder} में अपलोड रद्द किया गया',
    titleCancelling: '{folder} में अपलोड रद्द हो रहा है…',
    titleBlocked: '{folder} में अपलोड नहीं हो सका',
    progressSummary:
      '{total} में से {done} फ़ाइलें · {bytesTotal} में से {bytesDone}',
    timeLeft: 'लगभग {time} बाकी',
    summaryUploaded: '{count} अपलोड हुईं',
    summaryFailed: '{count} विफल',
    summaryCancelled: '{count} रद्द',
    view: 'देखें',
    showFiles: 'फ़ाइलें दिखाएँ',
    hideFiles: 'फ़ाइलें छिपाएँ',
    cancelAll: 'सभी रद्द करें',
    retryFailed: 'विफल फ़ाइलें फिर से आज़माएँ',
    retrySome: '{count} फिर से आज़माएँ',
    dismiss: 'बंद करें',
    cancelFile: '{name} रद्द करें',
    retryFile: '{name} फिर से आज़माएँ',
    filterAll: 'सभी',
    filterFailed: 'विफल ({count})',
    statusQueued: 'प्रतीक्षा में',
    statusUploading: '{total} में से {loaded}',
    statusRegistering: 'पूरा हो रहा है…',
    statusDone: 'अपलोड हो गया',
    statusCancelled: 'अपलोड रद्द किया गया',
    emptyFoldersFailed: 'खाली फ़ोल्डर नहीं बने: {count}',
    progressLabel: 'अपलोड की प्रगति',
    pasteMenuItem: 'पेस्ट करें',
    pastedImageName: 'पेस्ट की गई छवि {date}',
    pastedTextName: 'पेस्ट किया गया टेक्स्ट {date}',
    reasons: {
      storage_limit: 'वर्कस्पेस में पर्याप्त स्टोरेज नहीं है',
      uploads_busy: 'बहुत सारे अपलोड चल रहे हैं',
      network: 'नेटवर्क बाधित हुआ',
      unverified: 'अपलोड के दौरान बदल गई या सत्यापित नहीं हो सकी',
      permission: 'आप इस फ़ोल्डर में फ़ाइलें नहीं जोड़ सकते',
      invalid_path: 'इस फ़ोल्डर नाम का उपयोग नहीं किया जा सकता',
      unreadable: 'यह फ़ाइल पढ़ी नहीं जा सकी',
      too_large: 'अपलोड करने के लिए बहुत बड़ी है',
      active_content: 'सामग्री फ़ाइल प्रकार से मेल नहीं खाती',
      workspace_loading: 'वर्कस्पेस अभी लोड हो रहा था। फिर से कोशिश करें।',
      unknown: 'अपलोड विफल रहा',
    },
  },
  zh: {
    regionLabel: '上传',
    filterLabel: '显示的文件',
    titleUploading: '正在上传到 {folder}',
    titleDone: '已上传到 {folder}',
    titleFailed: '部分文件未能上传到 {folder}',
    titleCancelled: '已取消上传到 {folder}',
    titleCancelling: '正在取消上传到 {folder}…',
    titleBlocked: '无法上传到 {folder}',
    progressSummary: '{done}/{total} 个文件 · {bytesDone}/{bytesTotal}',
    timeLeft: '剩余约 {time}',
    summaryUploaded: '已上传 {count} 个',
    summaryFailed: '{count} 个失败',
    summaryCancelled: '已取消 {count} 个',
    view: '查看',
    showFiles: '显示文件',
    hideFiles: '隐藏文件',
    cancelAll: '全部取消',
    retryFailed: '重试失败项',
    retrySome: '重试 {count} 个',
    dismiss: '关闭',
    cancelFile: '取消 {name}',
    retryFile: '重试 {name}',
    filterAll: '全部',
    filterFailed: '失败 ({count})',
    statusQueued: '等待中',
    statusUploading: '{loaded}/{total}',
    statusRegistering: '正在完成…',
    statusDone: '已上传',
    statusCancelled: '已取消上传',
    emptyFoldersFailed: '未创建的空文件夹：{count}',
    progressLabel: '上传进度',
    pasteMenuItem: '粘贴',
    pastedImageName: '粘贴的图片 {date}',
    pastedTextName: '粘贴的文本 {date}',
    reasons: {
      storage_limit: '工作区存储空间不足',
      uploads_busy: '正在进行的上传过多',
      network: '网络中断',
      unverified: '上传期间已更改或无法验证',
      permission: '你无法向此文件夹添加文件',
      invalid_path: '无法使用此文件夹名称',
      unreadable: '无法读取此文件',
      too_large: '文件过大，无法上传',
      active_content: '文件内容与文件类型不符',
      workspace_loading: '工作区仍在加载。请重试。',
      unknown: '上传失败',
    },
  },
  'zh-TW': {
    regionLabel: '上傳',
    filterLabel: '顯示的檔案',
    titleUploading: '正在上傳到 {folder}',
    titleDone: '已上傳到 {folder}',
    titleFailed: '部分檔案未能上傳到 {folder}',
    titleCancelled: '已取消上傳到 {folder}',
    titleCancelling: '正在取消上傳到 {folder}…',
    titleBlocked: '無法上傳到 {folder}',
    progressSummary: '{done}/{total} 個檔案 · {bytesDone}/{bytesTotal}',
    timeLeft: '剩餘約 {time}',
    summaryUploaded: '已上傳 {count} 個',
    summaryFailed: '{count} 個失敗',
    summaryCancelled: '已取消 {count} 個',
    view: '查看',
    showFiles: '顯示檔案',
    hideFiles: '隱藏檔案',
    cancelAll: '全部取消',
    retryFailed: '重試失敗項目',
    retrySome: '重試 {count} 個',
    dismiss: '關閉',
    cancelFile: '取消 {name}',
    retryFile: '重試 {name}',
    filterAll: '全部',
    filterFailed: '失敗 ({count})',
    statusQueued: '等待中',
    statusUploading: '{loaded}/{total}',
    statusRegistering: '正在完成…',
    statusDone: '已上傳',
    statusCancelled: '已取消上傳',
    emptyFoldersFailed: '未建立的空資料夾：{count}',
    progressLabel: '上傳進度',
    pasteMenuItem: '貼上',
    pastedImageName: '貼上的圖片 {date}',
    pastedTextName: '貼上的文字 {date}',
    reasons: {
      storage_limit: '工作區儲存空間不足',
      uploads_busy: '進行中的上傳過多',
      network: '網路中斷',
      unverified: '上傳期間已變更或無法驗證',
      permission: '你無法將檔案新增到此資料夾',
      invalid_path: '無法使用此資料夾名稱',
      unreadable: '無法讀取此檔案',
      too_large: '檔案過大，無法上傳',
      active_content: '檔案內容與檔案類型不符',
      workspace_loading: '工作區仍在載入。請再試一次。',
      unknown: '上傳失敗',
    },
  },
};
